import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getCatalog } from "../i18n";
import { useMotionPolicy } from "../lib/hooks/useMotionPolicy";
import { DEMO_TOUR_SCRIPT } from "../mocks/demoTour";
import { usePresentedSnapshot } from "../presentation/hooks";
import { useNexusStore } from "../store/nexusStore";

const DEMO_BOOT_DELAY_MS = 1100;

type DemoStep =
  | { kind: "cosmos"; holdMs: number; label: string; eyebrow: string }
  | { kind: "node"; nodeId: string; holdMs: number }
  | { kind: "orbital"; nodeId: string; orbitalId: string; holdMs: number };

type ResolvedDemoStep = DemoStep & { label: string; eyebrow: string };

export function DemoTourDirector() {
  const snapshot = usePresentedSnapshot();
  const demoTourActive = useNexusStore((state) => state.demoTourActive);
  const focusNode = useNexusStore((state) => state.focusNode);
  const focusOrbital = useNexusStore((state) => state.focusOrbital);
  const focusCosmos = useNexusStore((state) => state.focusCosmos);
  const setIdleMode = useNexusStore((state) => state.setIdleMode);
  const setDemoTourActive = useNexusStore((state) => state.setDemoTourActive);
  const locale = useNexusStore((state) => state.locale);
  const motionPolicy = useMotionPolicy();
  const ui = getCatalog(locale).ui;
  const stepIndexRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepStartedAt, setStepStartedAt] = useState(0);
  const [tourReady, setTourReady] = useState(false);

  const sequence = useMemo<ResolvedDemoStep[]>(() => {
    if (!snapshot) return [];

    const nodes = new Map(snapshot.nodes.map((node) => [node.id, node]));
    const orbitals = new Map(snapshot.orbitalNodes.map((orbital) => [orbital.id, orbital]));
    const result: ResolvedDemoStep[] = [];

    for (const scripted of DEMO_TOUR_SCRIPT) {
      if (scripted.kind === "cosmos") {
        result.push({
          kind: "cosmos",
          holdMs: scripted.holdMs,
          label: "K·NEXUS",
          eyebrow: ui.heroEyebrow,
        });
        continue;
      }

      if (scripted.kind === "node") {
        const node = nodes.get(scripted.id);
        if (!node) continue;
        result.push({
          kind: "node",
          nodeId: node.id,
          holdMs: scripted.holdMs,
          label: node.label,
          eyebrow: node.eyebrow,
        });
        continue;
      }

      const orbital = orbitals.get(scripted.id);
      if (!orbital) continue;
      result.push({
        kind: "orbital",
        nodeId: scripted.parentId,
        orbitalId: orbital.id,
        holdMs: scripted.holdMs,
        label: orbital.label,
        eyebrow: orbital.eyebrow,
      });
    }

    return result;
  }, [snapshot, ui.heroEyebrow]);

  useEffect(() => {
    if (!demoTourActive || sequence.length === 0) return;

    const applyStep = (step: ResolvedDemoStep) => {
      if (step.kind === "cosmos") {
        focusCosmos();
        return;
      }
      if (step.kind === "node") {
        focusNode(step.nodeId);
        return;
      }
      focusOrbital(step.nodeId, step.orbitalId);
    };

    const runCurrentStep = () => {
      const index = stepIndexRef.current;
      const step = sequence[index];
      setStepIndex(index);
      setStepStartedAt(performance.now());
      setTourReady(true);
      applyStep(step);
      timeoutRef.current = window.setTimeout(() => {
        stepIndexRef.current =
          stepIndexRef.current === sequence.length - 1 ? Math.min(1, sequence.length - 1) : stepIndexRef.current + 1;
        runCurrentStep();
      }, step.holdMs);
    };

    setIdleMode(true);
    stepIndexRef.current = 0;
    timeoutRef.current = window.setTimeout(runCurrentStep, DEMO_BOOT_DELAY_MS);

    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      setTourReady(false);
      setIdleMode(false);
      focusCosmos();
    };
  }, [demoTourActive, sequence, focusNode, focusOrbital, focusCosmos, setIdleMode]);

  useEffect(() => {
    if (!demoTourActive) return;
    const exitOnManualNavigation = () => setDemoTourActive(false);
    window.addEventListener("knexus:scene-navigation", exitOnManualNavigation);
    return () => window.removeEventListener("knexus:scene-navigation", exitOnManualNavigation);
  }, [demoTourActive, setDemoTourActive]);

  const current = sequence[stepIndex] ?? null;

  return (
    <AnimatePresence>
      {demoTourActive && tourReady && current && (
        <motion.aside
          key={`${stepIndex}-${stepStartedAt}`}
          className="demo-tour-hud"
          aria-live="polite"
          initial={motionPolicy.reduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: motionPolicy.reduced ? 0 : 0.52 }}
        >
          <div className="demo-tour-hud__meta">
            <span>{ui.demoTour}</span>
            <b>
              {String(stepIndex + 1).padStart(2, "0")} / {String(sequence.length).padStart(2, "0")}
            </b>
          </div>
          <div className="demo-tour-hud__copy">
            <small>{current.eyebrow}</small>
            <strong>{current.label}</strong>
          </div>
          <div className="demo-tour-hud__progress" aria-hidden="true">
            <motion.span
              key={`${stepIndex}-${stepStartedAt}-progress`}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: motionPolicy.reduced ? 0 : current.holdMs / 1000, ease: "linear" }}
            />
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
